import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useCommonStore } from '@/stores/commonStore';
import ApiService from '@/services/ApiService';

import { Unternehmen } from '@/models/UnternehmenModels';
import { ArchivUser, ImportUserRequest, MergeUserRequest, User } from '@/models/BenutzerModels';

export const useUnternehmenStore: any = defineStore('unternehmenStore', () => {
  const commonStore = useCommonStore();

  const unternehmen = ref<Unternehmen[]>(sessionStorage.getItem('unternehmen') != null ? JSON.parse(sessionStorage.unternehmen) : []);

  //#region Unternehmen
  async function getUnternehmen() {
    commonStore.setStatus({ ...commonStore.status, getUnternehmenLoading: true, unternehmenErrorMsg: '' });

    return ApiService.getAllUnternehmen()
      .then((res) => {
        const data: Unternehmen[] = res.data;

        unternehmen.value = data;
        sessionStorage.setItem('unternehmen', JSON.stringify(unternehmen.value));
      })
      .catch((err) => {
        commonStore.status.unternehmenErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.getUnternehmenLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  async function unternehmenHasRealm(realm: string) {
    commonStore.setStatus({ ...commonStore.status, checkUnternehmenRealmLoading: true, unternehmenErrorMsg: '' });

    return ApiService.unternehmenHasRealm(realm)
      .then((res) => {
        const index = unternehmen.value.findIndex((u) => u.realm === realm);
        if (index !== -1) {
          unternehmen.value[index].hasIdpRealm = res.data as boolean;
          sessionStorage.setItem('unternehmen', JSON.stringify(unternehmen.value));
        }
      })
      .catch((err) => {
        commonStore.status.unternehmenErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.checkUnternehmenRealmLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  async function createUnternehmen(data: Unternehmen) {
    commonStore.setStatus({ ...commonStore.status, createUnternehmenLoading: true, unternehmenErrorMsg: '' });

    return ApiService.createUnternehmen(data)
      .then((res) => {
        const data: Unternehmen = res.data;

        unternehmen.value.push(data);
        sessionStorage.setItem('unternehmen', JSON.stringify(unternehmen.value));
      })
      .catch((err) => {
        commonStore.status.unternehmenErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.createUnternehmenLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  async function createUnternehmenRealm(id: number) {
    commonStore.setStatus({ ...commonStore.status, createUnternehmenRealmLoading: true, unternehmenErrorMsg: '' });

    return ApiService.createUnternehmenRealm(id)
      .then((_res) => {
        const index = unternehmen.value.findIndex((u) => u.id === id);
        if (index !== -1) {
          unternehmen.value[index].hasIdpRealm = true;
        }

        sessionStorage.setItem('unternehmen', JSON.stringify(unternehmen.value));
      })
      .catch((err) => {
        commonStore.status.unternehmenErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.createUnternehmenRealmLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  async function updateUnternehmen(data: Unternehmen) {
    commonStore.setStatus({ ...commonStore.status, updateUnternehmenLoading: true, unternehmenErrorMsg: '' });

    return ApiService.updateUnternehmen(data)
      .then((res) => {
        const data: Unternehmen = res.data;

        var index = unternehmen.value.findIndex((mapping) => mapping.id === data.id);
        if (index !== -1) {
          unternehmen.value.splice(index, 1, data);
        } else {
          unternehmen.value.push(data);
        }

        sessionStorage.setItem('unternehmen', JSON.stringify(unternehmen.value));
      })
      .catch((err) => {
        console.error(err);
        commonStore.status.unternehmenErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.updateUnternehmenLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  async function deleteUnternehmen(id: number) {
    commonStore.setStatus({ ...commonStore.status, deleteUnternehmenLoading: true, unternehmenErrorMsg: '' });

    return ApiService.deleteUnternehmen(id)
      .then(() => {
        unternehmen.value = unternehmen.value.filter((mapping) => mapping.id !== id);
        sessionStorage.setItem('unternehmen', JSON.stringify(unternehmen.value));
      })
      .catch((err) => {
        console.error(err);
        commonStore.status.unternehmenErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.deleteUnternehmenLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }
  //#endregion

  //#region Unternehmen Users
  const users = ref<User[]>([]);

  async function getUnternehmenUsers(realm: string) {
    commonStore.setStatus({ ...commonStore.status, getUnternehmenUsersLoading: true, unternehmenUserErrorMsg: '' });

    return ApiService.getAllKeycloakRealmUsers(realm)
      .then((res) => {
        const data: User[] = res.data;

        users.value = data;
      })
      .catch((err) => {
        commonStore.status.unternehmenUserErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.getUnternehmenUsersLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  const archivUsers = ref<ArchivUser[]>([]);

  async function getUnternehmenArchivUsers(id: number) {
    commonStore.setStatus({ ...commonStore.status, getAllArchivUserLoading: true, archivUserErrorMsg: '' });

    return ApiService.getAllUnternehmenArchivUsers(id)
      .then((res) => {
        const data: ArchivUser[] = res.data;

        archivUsers.value = data;
      })
      .catch((err) => {
        commonStore.status.archivUserErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.getAllArchivUserLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  async function mergeArchivUser(data: MergeUserRequest) {
    commonStore.setStatus({ ...commonStore.status, updateArchivUserLoading: true, archivUserErrorMsg: '' });

    return ApiService.mergeArchivUser(data)
      .then((res) => {
        const data: ArchivUser[] = res.data;

        archivUsers.value = data;
      })
      .catch((err) => {
        commonStore.status.archivUserErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.updateArchivUserLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  async function importArchivUser(data: ImportUserRequest) {
    commonStore.setStatus({ ...commonStore.status, updateArchivUserLoading: true, archivUserErrorMsg: '' });

    return ApiService.importArchivUser(data)
      .then((res) => {
        const data: ArchivUser[] = res.data;

        archivUsers.value = data;
      })
      .catch((err) => {
        commonStore.status.archivUserErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.updateArchivUserLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }
  //#endregion

  return {
    //#region Unternehmen
    unternehmen,

    getUnternehmen,
    unternehmenHasRealm,
    createUnternehmen,
    createUnternehmenRealm,
    updateUnternehmen,
    deleteUnternehmen,
    //#endregion

    //#region Unternehmen Users
    users,
    getUnternehmenUsers,

    archivUsers,
    getUnternehmenArchivUsers,
    mergeArchivUser,
    importArchivUser,
    //#endregion
  };
});
